import React, { useState } from "react";
import { createPortal } from "react-dom";
import { MarkPriceData } from "../../sdk/types/data";

interface PriceRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  minPrice: number;
  maxPrice: number;
  onMinPriceChange: (price: number) => void;
  onMaxPriceChange: (price: number) => void;
  markPrice: MarkPriceData | null;
  hasManualPriceChange: boolean;
  onResetToAuto: () => void;
  orderMode: "random" | "curve";
  onOrderModeChange: (mode: "random" | "curve") => void;
  curveIntensity: number;
  onCurveIntensityChange: (intensity: number) => void;
  roundingStep: number;
  onRoundingStepChange: (step: number) => void;
}

export const PriceRangeModal: React.FC<PriceRangeModalProps> = ({
  isOpen,
  onClose,
  minPrice,
  maxPrice,
  onMinPriceChange,
  onMaxPriceChange,
  markPrice,
  hasManualPriceChange,
  onResetToAuto,
  orderMode,
  onOrderModeChange,
  curveIntensity,
  onCurveIntensityChange,
  roundingStep,
  onRoundingStepChange,
}) => {

  // Calculate auto values with current rounding step
  const calculateAutoMinPrice = (markPriceValue: number): number => {
    return Math.round((markPriceValue / 1.5) / roundingStep) * roundingStep;
  };

  const calculateAutoMaxPrice = (markPriceValue: number): number => {
    return Math.round((markPriceValue * 1.5) / roundingStep) * roundingStep;
  };

  if (!isOpen) return null;

  const markPriceValue = markPrice?.mark_price ?? null;
  const hasMarkPrice = markPrice !== null && markPriceValue !== null;

  const autoMinPrice = hasMarkPrice ? calculateAutoMinPrice(markPriceValue!) : null;
  const autoMaxPrice = hasMarkPrice ? calculateAutoMaxPrice(markPriceValue!) : null;

  return typeof document !== "undefined"
    ? createPortal(
        <div
          className="modal-overlay"
          onClick={onClose}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "rgba(20, 20, 30, 0.95)",
              borderRadius: "12px",
              padding: 0,
              minWidth: "400px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <div
              className="modal-header"
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  color: "rgba(255, 255, 255, 0.9)",
                }}
              >
                Price Range Configuration
              </h3>
            </div>
            <div className="modal-body" style={{ padding: "2rem 1.5rem" }}>
              <div className="trading-test-price-range-controls">
                {/* Rounding Step Input */}
                <div>
                  <label>
                    Rounding Step ($):
                    <input
                      type="number"
                      value={roundingStep}
                      onChange={(e) =>
                        onRoundingStepChange(Math.max(1, parseFloat(e.target.value) || 100))
                      }
                      min={1}
                      step={10}
                      className="trading-test-input"
                    />
                  </label>
                  <small style={{ color: "#888", display: "block", marginTop: "4px" }}>
                    Prices will be rounded to the nearest ${roundingStep}
                  </small>
                </div>

                <div>
                  <label>
                    Min Price (USDC):
                    <input
                      type="number"
                      value={minPrice}
                      onChange={(e) => {
                        onMinPriceChange(parseFloat(e.target.value) || 10000);
                      }}
                      min={1000}
                      max={maxPrice - 1000}
                      className="trading-test-input"
                      disabled={!hasMarkPrice}
                      title={hasMarkPrice ? `Auto-managed by mark price (mark price / 1.5, rounded to $${roundingStep})` : "Enter min price manually"}
                    />
                  </label>
                  {hasMarkPrice && autoMinPrice !== null && markPriceValue !== null && (
                    <small
                      style={{
                        color: "#888",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      {hasManualPriceChange ? (
                        <>
                          Manual override (Auto:{" "}
                          {markPriceValue!.toLocaleString()} / 1.5 ={" "}
                          {autoMinPrice.toLocaleString()})
                        </>
                      ) : (
                        <>
                          Auto: {markPriceValue!.toLocaleString()} / 1.5 ={" "}
                          {minPrice.toLocaleString()}
                        </>
                      )}
                    </small>
                  )}
                </div>
                <div>
                  <label>
                    Max Price (USDC):
                    <input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => {
                        onMaxPriceChange(parseFloat(e.target.value) || 100000);
                      }}
                      min={minPrice + 1000}
                      max={1000000}
                      className="trading-test-input"
                      disabled={!hasMarkPrice}
                      title={hasMarkPrice ? `Auto-managed by mark price (mark price × 1.5, rounded to $${roundingStep})` : "Enter max price manually"}
                    />
                  </label>
                  {hasMarkPrice && markPriceValue !== null && autoMaxPrice !== null && (
                    <small
                      style={{
                        color: "#888",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      {hasManualPriceChange ? (
                        <>
                          Manual override (Auto:{" "}
                          {markPriceValue.toLocaleString()} × 1.5 ={" "}
                          {autoMaxPrice.toLocaleString()})
                        </>
                      ) : (
                        <>
                          Auto: {markPriceValue.toLocaleString()} × 1.5 ={" "}
                          {maxPrice.toLocaleString()}
                        </>
                      )}
                    </small>
                  )}
                  {!hasMarkPrice && (
                    <small
                      style={{
                        color: "#888",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      Mark price not available. Enter prices manually.
                    </small>
                  )}
                </div>
                <div>
                  <label>
                    Order Mode:
                    <select
                      value={orderMode}
                      onChange={(e) =>
                        onOrderModeChange(e.target.value as "random" | "curve")
                      }
                      className="trading-test-select"
                    >
                      <option value="random">Random</option>
                      <option value="curve">
                        Curve (Buy more at lower prices)
                      </option>
                    </select>
                  </label>
                </div>
                {orderMode === "curve" && (
                  <div>
                    <label>
                      Curve Intensity: {curveIntensity}
                      <input
                        type="range"
                        value={curveIntensity}
                        onChange={(e) =>
                          onCurveIntensityChange(parseFloat(e.target.value))
                        }
                        min={1}
                        max={5}
                        step={0.5}
                        className="trading-test-range"
                      />
                      <small>
                        Higher = more aggressive buying at lower prices
                      </small>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div
              className="modal-footer"
              style={{
                padding: "1rem 1.5rem",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              {hasManualPriceChange && hasMarkPrice && (
                <button
                  onClick={onResetToAuto}
                  className="btn btn-secondary"
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "rgba(77, 171, 247, 0.2)",
                    color: "rgba(255, 255, 255, 0.9)",
                    cursor: "pointer",
                  }}
                >
                  Reset to Auto
                </button>
              )}
              <div
                style={{ display: "flex", gap: "1rem", marginLeft: "auto" }}
              >
                <button
                  onClick={onClose}
                  className="btn btn-secondary"
                  style={{
                    padding: "0.5rem 1rem",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "rgba(255, 255, 255, 0.1)",
                    color: "rgba(255, 255, 255, 0.9)",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;
};















